import type { ChangeEvent, MouseEvent } from "react"
import { FYIService } from "@gap/backend/app-services/fyi/FYIService"
import { FYIDto } from "@gap/backend/app-services/fyi/models/FYIDto"
import { notNull } from "@gap/utils/utils"
import { namingConfig } from "@gap/utils/config/naming-config"
import { ActiveIntegrations, Analytics } from "analytics/analytics"
import ShareAnalytics from "analytics/ShareAnalytics"
import { apiClient } from "api/trpc/trpc-client"
import { IDBFYIClientsCache } from "cache/idb/IDBFYIClientsCache"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { ClientSourceActions } from "features/org/edit/integrations/AdvisorIntegrationCard"
import { ClientSyncState } from "features/org/edit/integrations/ClientSyncAction"
import { confirmClientSourceSwitch } from "features/clients/confirmClientSourceSwitch"
import WebSocketStore from "websockets/WebSocketStore"
import { GapDialogAction } from "reusable/gap-dialog/GapDialogActionButton"
import Loader from "reusable/mobx-helpers/Loader"
import ModalState from "reusable/mobx-helpers/ModalState"
import { formHandler, FormHandlerConfig } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { snackbarError, snackbarInfo, snackbarSuccess, snackbarWarning } from "reusable/mui/snackbar/snackbar"
import { Sentry } from "sentry/sentry"

export type AdvisorIntegrationFYISource = 'integrations' | 'share-dialog' | 'create-client-modal'

/** What the org syncs from FYI: documents alone, or clients as well. */
export type FYISyncMode = 'documents' | 'clients'

export interface FYISyncModeOption {
   value: FYISyncMode
   title: string
   description: string
   badge?: { label: string; colorVariant: 'accent' | 'success' }
   chips: string[]
}

const SAVE_SYNC_MODE_ERROR = 'We had trouble changing what syncs from FYI. Please try again.'

interface AdvisorIntegrationFYIStoreProps {
   source: AdvisorIntegrationFYISource
   orgGuardStore: OrgGuardStore
   /** Receives the sync completion event; the share dialog passes none. */
   webSocketStore?: WebSocketStore
}

export default class AdvisorIntegrationFYIStore {
   connectDialog = new ModalState({
      onClose: () => this.resetFields()
   })
   manageDialog = new ModalState()
   clientLoader = new Loader(false)
   loader = new Loader(true)
   connectLoader = new Loader()
   clientSyncLoader = new Loader()
   manageLoader = new Loader()
   status: FYIService.status = 'not connected'
   /** FYI clients loaded so far; grows page by page while `clientLoader` is loading. */
   FYIClients: FYIDto.clientMinimal[] | undefined
   /** True from pressing Sync now until the server reports that sync finished. */
   isSyncing = false
   /** True from queueing FYI's first full sync this session until it completes, FYI disconnects, or clients stop syncing. */
   firstSyncRunning = false
   /** Shared by the connect and manage dialogs, which never open together; each open resets it. */
   syncMode: FYISyncMode = 'clients'
   connectStep: 1 | 2 = 1
   switchAcknowledged = false
   /** XPM client uuid sent with the first page so the matching FYI client arrives before the rest. */
   clientXeroId: string | undefined
   fields = {
      accessId: '',
      accessSecret: ''
   }
   sp

   constructor(sp: AdvisorIntegrationFYIStoreProps) {
      this.sp = sp
   }

   get formHandler() {
      return formHandler(this.fields, {
         accessId: { type: 'string' },
         accessSecret: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   get hasClientSyncAccess() {
      return this.sp.orgGuardStore.hasFyiClientSyncAccess
   }

   /** The two-step connect (choose what to sync, then keys); the share dialog and orgs without the toggle keep the keys-only form. */
   get usesStepFlow() {
      return this.hasClientSyncAccess && this.sp.source !== 'share-dialog'
   }

   /** True while FYI is the org's client source. */
   get isClientSource() {
      return this.sp.orgGuardStore.userOrg.syncVia === 'fyi'
   }

   get xpmIsClientSource() {
      return this.sp.orgGuardStore.userOrg.syncVia === 'xpm'
   }

   get isReconnect() {
      return this.status === 'connection lost'
   }

   /** Taking the source over from XPM needs the user's explicit acknowledgement in the dialog. */
   get needsSwitchAcknowledgement() {
      return this.syncMode === 'clients' && this.xpmIsClientSource
   }

   /** Whether connecting also makes FYI the client source. */
   get shouldSetClientSource() {
      return this.usesStepFlow && this.syncMode === 'clients' && !this.isClientSource
   }

   get keysMissing() {
      return !this.fields.accessId || !this.fields.accessSecret
   }

   /** Whether the integrations page card shows its capability rows, connected or not. */
   get showClientSourceRow() {
      return this.sp.source === 'integrations' && this.hasClientSyncAccess
   }

   get showManageAction() {
      return this.showClientSourceRow && this.status === 'connected'
   }

   get clientsRowState(): ClientSyncState {
      if (this.firstSyncRunning) return 'first-sync'
      return this.isSyncing ? 'syncing' : 'idle'
   }

   get cardSubTitle() {
      if (!this.hasClientSyncAccess) return 'Store client files directly in FYI'
      if (this.status === 'not connected') return 'Sync documents or clients and documents'
      return this.isClientSource ? 'Syncing clients and documents' : 'Storing documents in FYI'
   }

   get connectDialogTitle() {
      if (!this.usesStepFlow) return 'Connect to FYI'
      return this.connectStep === 1 ? 'What do you want to sync from FYI?' : 'Enter your FYI keys'
   }

   get manageSelectionUnchanged() {
      return (this.syncMode === 'clients') === this.isClientSource
   }

   get showCancelButton() {
      return !this.usesStepFlow || this.connectStep === 1
   }

   get primaryGapDialogButton(): GapDialogAction {
      if (this.usesStepFlow && this.connectStep === 1) {
         return {
            name: 'Next',
            callback: this.goToStepTwo,
            disabled: this.needsSwitchAcknowledgement && !this.switchAcknowledged,
            notCloseAutomatically: true
         }
      }
      return {
         name: this.connectButtonLabel,
         callback: this.connect,
         disabled: this.keysMissing,
         loading: this.loader.isLoading || this.connectLoader.isLoading,
         notCloseAutomatically: true
      }
   }

   get secondaryGapDialogButton(): GapDialogAction | undefined {
      if (!this.usesStepFlow || this.connectStep !== 2) return undefined
      return {
         name: 'Back',
         callback: this.goToStepOne,
         disabled: this.connectLoader.isLoading,
         notCloseAutomatically: true
      }
   }

   get managePrimaryGapDialogButton(): GapDialogAction {
      return {
         name: 'Save changes',
         callback: this.saveSyncMode,
         disabled: this.manageSelectionUnchanged || (this.needsSwitchAcknowledgement && !this.switchAcknowledged),
         loading: this.manageLoader.isLoading,
         notCloseAutomatically: true
      }
   }

   /** The card's client source state and actions; the share dialog has none. */
   get clientSource(): ClientSourceActions | undefined {
      if (this.sp.source === 'share-dialog') return undefined
      return {
         isCurrent: this.isClientSource,
         onSyncNow: this.syncClientsNow,
         syncing: this.isSyncing,
         onSetAsSource: this.setAsClientSource,
         settingSource: this.clientSyncLoader.isLoading,
      }
   }

   /** The connect dialog's option cards; the copy changes while XPM holds the source. */
   get connectSyncModeOptions(): FYISyncModeOption[] {
      if (this.xpmIsClientSource) {
         return [
            { value: 'documents', title: 'Documents only', description: 'Documents are stored in FYI. Clients keep syncing from Xero Practice Manager.', chips: ['Documents'] },
            { value: 'clients', title: 'Clients and documents', description: 'FYI becomes your client source, replacing Xero Practice Manager.', badge: { label: 'Needs FYI Elite', colorVariant: 'accent' }, chips: ['Clients', 'Documents'] },
         ]
      }
      return [
         { value: 'documents', title: 'Documents only', description: `Documents created in ${namingConfig.product.singular} are stored in FYI. Your clients keep coming from where they do today.`, chips: ['Documents'] },
         { value: 'clients', title: 'Clients and documents', description: `FYI becomes your client source. Clients sync from FYI, and documents created in ${namingConfig.product.singular} are stored there too.`, badge: { label: 'FYI Elite', colorVariant: 'accent' }, chips: ['Clients', 'Documents'] },
      ]
   }

   /** The manage dialog's option cards, with the current mode marked. */
   get manageSyncModeOptions(): FYISyncModeOption[] {
      const current = { label: 'Current', colorVariant: 'success' } as const
      const elite = { label: this.xpmIsClientSource ? 'Needs FYI Elite' : 'FYI Elite', colorVariant: 'accent' } as const
      return [
         {
            value: 'documents',
            title: 'Documents only',
            description: "Documents are stored in FYI. You'll choose another client source, or add clients manually.",
            badge: this.isClientSource ? undefined : current,
            chips: ['Documents'],
         },
         {
            value: 'clients',
            title: 'Clients and documents',
            description: this.isClientSource ? 'FYI is your client source, and documents are stored there.' : 'FYI becomes your client source, and documents are stored there.',
            badge: this.isClientSource ? current : elite,
            chips: ['Clients', 'Documents'],
         },
      ]
   }

   private get connectButtonLabel() {
      const syncsClients = this.shouldSetClientSource
      if (this.isReconnect) return syncsClients ? 'Re-connect and sync clients' : 'Re-connect'
      return syncsClients ? 'Connect and sync clients' : 'Connect'
   }

   /** Opens the connect dialog at the choice step, or straight at the keys when re-connecting or without the two-step flow. */
   openConnectDialog = () => {
      this.connectStep = this.usesStepFlow && !this.isReconnect ? 1 : 2
      this.syncMode = this.isClientSource || !this.isReconnect ? 'clients' : 'documents'
      this.switchAcknowledged = false
      this.connectDialog.open()
   }

   openManageDialog = () => {
      this.syncMode = this.isClientSource ? 'clients' : 'documents'
      this.switchAcknowledged = false
      this.manageDialog.open()
   }

   selectSyncModeHandler = (event: MouseEvent<HTMLElement>) => {
      const mode = event.currentTarget.getAttribute('data-sync-mode')
      if (mode !== 'documents' && mode !== 'clients') return
      this.syncMode = mode
      this.switchAcknowledged = false
   }

   switchAcknowledgedChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
      this.switchAcknowledged = event.target.checked
   }

   goToStepOne = () => {
      this.connectStep = 1
   }

   goToStepTwo = () => {
      this.connectStep = 2
   }

   resetFields = () => {
      this.fields.accessId = ''
      this.fields.accessSecret = ''
   }

   validate = () => {
      return this.formHandler.validate(FYIDto.accessTokenSchema)
   }

   /** Stores the keys, then makes FYI the client source when that is what the user chose. */
   connect = async () => {
      if (!this.validate()) return
      const wasReconnect = this.isReconnect
      try {
         await this.connectLoader.executeLoading(async () => {
            const result = await apiClient.fyi.setToken.mutate({
               accessId: this.fields.accessId,
               accessSecret: this.fields.accessSecret
            })
            if (result.status !== 'successful') {
               snackbarError(`We had trouble connecting to FYI. ${result.message}`)
               return
            }
            await this.recordConnected()
            if (!this.shouldSetClientSource) {
               this.connectDialog.close()
               snackbarSuccess(wasReconnect ? 'FYI re-connected.' : 'FYI connected.')
               return
            }
            const switched = await this.makeClientSource()
            this.connectDialog.close()
            if (!switched) {
               snackbarError('FYI is connected, but we had trouble making it your client source. Use Change what syncs to try again.')
               return
            }
            snackbarSuccess(this.firstSyncRunning ? 'FYI connected. Clients now sync from FYI, and the first sync has started.' : 'FYI connected. Clients now sync from FYI.')
         })
      } catch (error) {
         Sentry.reportCustomMessage('fyi-connect', { error: String(error) })
         snackbarError('We had trouble connecting to FYI. Please try again.')
      }
      await this.fetchClients()
   }

   disconnect = async () => {
      await this.loader.executeLoading(async () => {
         await apiClient.fyi.disconnect.mutate()
         const orgId = notNull(this.sp.orgGuardStore.orgId)
         Analytics.setActiveIntegrations(orgId, { [ActiveIntegrations.FYI]: false })
         await IDBFYIClientsCache.invalidate({ orgId })
         this.status = 'not connected'
         this.firstSyncRunning = false
         if (this.sp.orgGuardStore.userOrg.syncVia === 'fyi') {
            this.sp.orgGuardStore.updateUserOrgData({ syncVia: undefined })
         }
         snackbarSuccess('Sucessfully disconnected with FYI')
      })
   }

   fetch = async (params?: { xeroId?: string }) => {
      this.clientXeroId = params?.xeroId
      await this.loader.executeLoading(async () => {
         this.status = await apiClient.fyi.getConnectionStatus.query()
      })
      await this.fetchClients()
      await this.fetchClientSyncStatus()
   }

   fetchClientSyncStatus = async () => {
      if (this.sp.source === 'share-dialog') {
         return
      }
      try {
         // Listening before the status is read, so a sync that finishes in between is not missed.
         await this.subscribeToSyncCompletion()
         const clientSync = await apiClient.fyi.getClientSyncStatus.query()
         this.isSyncing = clientSync.syncInProgress
      } catch (error) {
         Sentry.reportCustomMessage('fyi-fetch-client-sync-status', { error: String(error) })
      }
   }

   /** Makes FYI the client source from the create client modal, after confirming the switch when another integration holds it. */
   setAsClientSource = async () => {
      const previous = this.sp.orgGuardStore.userOrg.syncVia
      if (previous && !(await confirmClientSourceSwitch({ from: previous, to: 'fyi' }))) return
      await this.clientSyncLoader.executeLoading(async () => {
         if (!(await this.makeClientSource())) {
            snackbarError('We had trouble switching the client source to FYI. Please try again.')
            return
         }
         snackbarSuccess(this.firstSyncRunning ? 'Clients will now sync from FYI, and the first sync has started' : 'Clients will now sync from FYI')
      })
   }

   /** Applies the manage dialog's choice: stops syncing clients, or makes FYI the client source. */
   saveSyncMode = async () => {
      if (this.manageSelectionUnchanged) return
      try {
         await this.manageLoader.executeLoading(async () => {
            if (this.syncMode === 'documents') {
               await apiClient.fyi.setClientSync.mutate({ syncClients: false })
               this.sp.orgGuardStore.updateUserOrgData({ syncVia: undefined })
               this.firstSyncRunning = false
               this.manageDialog.close()
               return
            }
            if (!(await this.makeClientSource())) {
               snackbarError(SAVE_SYNC_MODE_ERROR)
               return
            }
            this.manageDialog.close()
         })
      } catch (error) {
         Sentry.reportCustomMessage('fyi-save-sync-mode', { error: String(error) })
         snackbarError(SAVE_SYNC_MODE_ERROR)
      }
   }

   syncClientsNow = async () => {
      if (this.isSyncing) return
      this.isSyncing = true
      try {
         const result = await apiClient.fyi.syncClientsNow.mutate()
         // The button keeps spinning until the completion event arrives over the websocket.
         await this.subscribeToSyncCompletion()
         if (!result.queued) {
            snackbarInfo('A client sync from FYI is already running')
         }
      } catch (error) {
         this.isSyncing = false
         Sentry.reportCustomMessage('fyi-client-sync-queue', { error: String(error) })
         snackbarError('We had trouble starting the FYI client sync. Please try again.')
      }
   }

   fetchClients = async () => {
      if (this.status !== 'connected' || this.sp.source !== 'share-dialog') {
         return
      }
      await this.clientLoader.executeLoading(async () => {
         this.FYIClients = []
         const clients = await IDBFYIClientsCache.get({
            orgId: notNull(this.sp.orgGuardStore.orgId),
            xeroId: this.clientXeroId,
            onClientsLoaded: this.setFYIClients,
         })
         if (clients) {
            this.setFYIClients(clients)
         }
      })
   }

   /** Stops listening for sync completion once the card is gone, so no snackbar fires from a page that closed. */
   dispose = () => {
      const { webSocketStore, orgGuardStore } = this.sp
      if (!webSocketStore) return
      void webSocketStore.removeSubscriptions({
         orgId: orgGuardStore.userData.userOrg.orgId,
         userId: orgGuardStore.userData.id,
         subscriptions: [{ event: 'client-sync-completed', entityId: orgGuardStore.userData.userOrg.orgId }],
      })
   }

   private recordConnected = async () => {
      const orgId = notNull(this.sp.orgGuardStore.orgId)
      Analytics.setActiveIntegrations(orgId, { [ActiveIntegrations.FYI]: true })
      await IDBFYIClientsCache.invalidate({ orgId })
      this.status = 'connected'
      if (this.sp.source === 'share-dialog') {
         ShareAnalytics.connectToFYIViaShareComponent()
      }
   }

   /** Makes FYI the client source and starts watching its first sync; false when the server refused. */
   private makeClientSource = async (): Promise<boolean> => {
      try {
         const result = await apiClient.clientSource.set.mutate({ source: 'fyi' })
         this.sp.orgGuardStore.updateUserOrgData({ syncVia: 'fyi' })
         if (result.queued) {
            this.firstSyncRunning = true
            this.isSyncing = true
         }
         // The status carries the sync lock, which drives the spinner until completion arrives.
         await this.fetchClientSyncStatus()
         return true
      } catch (error) {
         Sentry.reportCustomMessage('fyi-set-client-source-switch', { error: String(error) })
         return false
      }
   }

   private setFYIClients = (clients: FYIDto.clientMinimal[]) => {
      this.FYIClients = clients
   }

   private subscribeToSyncCompletion = async () => {
      const { webSocketStore, orgGuardStore } = this.sp
      if (!webSocketStore) return
      await webSocketStore.addSubscriptions({
         orgId: orgGuardStore.userData.userOrg.orgId,
         userId: orgGuardStore.userData.id,
         subscriptions: [{
            event: 'client-sync-completed',
            entityId: orgGuardStore.userData.userOrg.orgId,
            callback: async response => {
               this.isSyncing = false
               this.firstSyncRunning = false
               if (response.message === 'sync_failed') {
                  snackbarError('We had trouble syncing your clients from FYI. Please try again.')
                  return
               }
               if (response.message === 'sync_partial') {
                  snackbarWarning('Clients synced from FYI, but some could not be updated. Check the client sync log for details.')
                  return
               }
               snackbarSuccess('Clients synced from FYI')
            },
         }],
      })
   }

}

export const { Provider: AdvisorIntegrationFYIStoreProvider, useProvidedStore: useAdvisorIntegrationFYIStore } = createStoreContext(AdvisorIntegrationFYIStore)
